/**
 * Client half of the form pipeline. The server half is
 * `functions/_shared/forms.js` — read that first; it explains why the browser
 * sends a topic key rather than a listmonk list UUID, and why newsletter
 * consent lives in listmonk rather than HubSpot.
 */

import type { FirstTouch } from "@/lib/links";

/** Public newsletter topics. Investor updates are curated by hand, not signed up for. */
export type NewsletterTopic = "weekly" | "other";

export type LeadFormKey = "contact" | "demo" | "been-breached";

export interface NewsletterPayload {
  email: string;
  name?: string;
  topics?: NewsletterTopic[];
  /** Honeypot. Always sent, always empty unless a bot filled it in. */
  companyWebsite?: string;
  turnstileToken?: string;
}

export interface LeadPayload {
  formKey: LeadFormKey;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  jobTitle?: string;
  phone?: string;
  message?: string;
  /** Extra context worth putting in front of whoever picks the lead up. */
  context?: string;
  pageUrl?: string;
  /** Campaign the visitor first landed with — `pageUrl` has usually lost it by now. */
  firstTouch?: FirstTouch;
  newsletter?: boolean;
  companyWebsite?: string;
  turnstileToken?: string;
}

export interface NewsletterResult {
  /** True when listmonk sent a confirmation email the visitor must click. */
  hasOptin: boolean;
}

export interface LeadResult {
  /** True when the newsletter opt-in also succeeded. The lead is saved either way. */
  subscribed: boolean;
}

/**
 * Why a submission failed, as a fixed code that is safe to send to analytics:
 * `network`, or `http_<status>` (`http_400` validation, `http_403` bot check,
 * `http_429` rate limit, `http_5xx`...). Never the server's message.
 */
export type FormFailureReason = "network" | `http_${number}` | "unknown";

/** Thrown with a message written for the visitor. */
export class FormSubmitError extends Error {
  readonly reason: FormFailureReason;

  constructor(message: string, reason: FormFailureReason) {
    super(message);
    this.reason = reason;
  }
}

export function failureReason(error: unknown): FormFailureReason {
  return error instanceof FormSubmitError ? error.reason : "unknown";
}

async function postJson<T>(url: string, payload: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new FormSubmitError(
      "We couldn't reach the server. Check your connection and try again.",
      "network"
    );
  }

  const body = await response.json().catch(() => null);

  if (!response.ok || !body?.ok) {
    throw new FormSubmitError(
      typeof body?.error === "string" ? body.error : "Something went wrong. Please try again.",
      `http_${response.status}`
    );
  }

  return body as T;
}

export function submitNewsletter(payload: NewsletterPayload) {
  return postJson<NewsletterResult>("/api/newsletter/subscribe", payload);
}

export function submitLead(payload: LeadPayload) {
  return postJson<LeadResult>("/api/leads", payload);
}
